import { PAGE_URLS } from "utils/constants";
import { INTENT_KEYS } from "../IntentCapture/constants";

// CAT-510 — every `?source=` value PTFW is entered with, and where its back
// affordance should land. Unknown or legacy values fall back to Home.
export const PTFW_SOURCES = {
  HOME: "home",
  SHOP: "shop",
  COLLAB_FTUX: "collab_ftux",
  INTENT: "intent",
  BRAND_CAMPAIGN_UH_OH: "brand_campaign_uh_oh",
  // CAT-512 — one source for every DIY step screen (Select Post, Add Details /
  // Add Products, Preview Details). They share a header, a step counter and a
  // single linear flow, so splitting them would buy a GA dimension nobody asked
  // for and three dock offsets that are all 84.
  DIY: "diy",
};

/*
 * CAT-513 — the entry trigger a pill impression is reported under.
 *
 * Finer than `PTFW_SOURCES` on purpose, and only for analytics. `diy` stays one
 * source because the dock offset, the back URL and the compact pill form are
 * identical on all three DIY screens; the readout still needs to tell them
 * apart, so the split lives here and nowhere else.
 */
export const PTFW_PILL_TRIGGERS = {
  HOME_SCROLL: "home_scroll",
  DIY_SELECT_POST: "diy_select_post",
  DIY_ADD_DETAILS: "diy_add_details",
  DIY_PREVIEW: "diy_preview",
  SHOP: "shop",
  COLLAB_FTUX: "collab_ftux",
  BRAND_CAMPAIGN_UH_OH: "brand_campaign_uh_oh",
};

/**
 * The trigger a surface reports when it passes no explicit one. Only the DIY
 * screens have to name themselves, because they share a source.
 */
export const PILL_TRIGGER_BY_SOURCE = {
  [PTFW_SOURCES.HOME]: PTFW_PILL_TRIGGERS.HOME_SCROLL,
  [PTFW_SOURCES.SHOP]: PTFW_PILL_TRIGGERS.SHOP,
  [PTFW_SOURCES.COLLAB_FTUX]: PTFW_PILL_TRIGGERS.COLLAB_FTUX,
  [PTFW_SOURCES.BRAND_CAMPAIGN_UH_OH]: PTFW_PILL_TRIGGERS.BRAND_CAMPAIGN_UH_OH,
};

/** `trigger` prop if given, else the surface's default, else the raw source. */
export const resolvePillTrigger = (source, trigger) =>
  trigger || PILL_TRIGGER_BY_SOURCE[source] || source;

export const PTFW_SOURCE_BACK_URLS = {
  [PTFW_SOURCES.HOME]: PAGE_URLS.HOME,
  [PTFW_SOURCES.SHOP]: PAGE_URLS.SHOP,
  // CollabFTUX is a one-way funnel — its own exit is Home, so back is Home.
  [PTFW_SOURCES.COLLAB_FTUX]: PAGE_URLS.HOME,
  // The intent screen is mandatory and must never be reachable by going back.
  [PTFW_SOURCES.INTENT]: PAGE_URLS.HOME,
  [PTFW_SOURCES.BRAND_CAMPAIGN_UH_OH]: PAGE_URLS.BRAND_CAMPAIGN_UH_OH,
  // The DIY flow is mid-creation and its screens take ids in their paths, so
  // there is no single URL to send someone back to. Shop is where the flow
  // lands on publish and on every bail-out already, so back goes there too.
  [PTFW_SOURCES.DIY]: PAGE_URLS.SHOP,
};

export const STEP_KEYS = {
  ENGAGE_SETUP: "engage_setup",
  HOW_YOU_EARN: "how_you_earn",
  CHOOSE_YOUR_PATH: "choose_your_path",
  PLAN_YOUR_CONTENT: "plan_your_content",
  CREATE_YOUR_WISHLINK: "create_your_wishlink",
  SHARE_YOUR_WISHLINK: "share_your_wishlink",
  TEST_ENGAGE: "test_engage",
  YOURE_ALL_SET: "youre_all_set",
};

export const STEP_STATUS = {
  DONE: "done",
  IN_PROGRESS: "in_progress",
  LOCKED: "locked",
};

export const JOURNEY_PATHS = {
  UNDECIDED: "undecided",
  TRIAL: "trial",
  NEW_CONTENT: "new_content",
};

// FontAwesome icon class for each step circle (update class names as needed)
export const STEP_ICONS = {
  [STEP_KEYS.ENGAGE_SETUP]: "fa-solid fa-comment-dots",
  [STEP_KEYS.HOW_YOU_EARN]: "fa-solid fa-graduation-cap",
  [STEP_KEYS.CHOOSE_YOUR_PATH]: "fa-solid fa-route",
  [STEP_KEYS.PLAN_YOUR_CONTENT]: "fa-solid fa-tags",
  [STEP_KEYS.CREATE_YOUR_WISHLINK]: "fa-solid fa-link",
  [STEP_KEYS.SHARE_YOUR_WISHLINK]: "fa-solid fa-share-nodes",
  [STEP_KEYS.TEST_ENGAGE]: "fa-solid fa-circle-check",
  [STEP_KEYS.YOURE_ALL_SET]: "fa-solid fa-trophy",
};

// Asset filenames for step content images (served via getAssetUrl)
export const STEP_ASSETS = {
  ENGAGE_SETUP_DONE: "setup_journey_engage_done.png",
  HOW_YOU_EARN_INFOGRAPHIC: "how_you_earn_v3.png",
  CHOOSE_PATH_TRIAL_ICON: "setup_journey_trial_icon.png",
  CHOOSE_PATH_NEW_CONTENT_ICON: "setup_journey_new_content_icon.png",
  ALL_SET_TROPHY: "golden-trophy.svg",
};

export const STEP_META = {
  [STEP_KEYS.ENGAGE_SETUP]: {
    title: "Engage Setup",
    subtitle: "Auto-DM is ready",
  },
  [STEP_KEYS.HOW_YOU_EARN]: {
    title: "How You Earn",
    subtitle: "Unlock paid collabs, rewards, and more as you make more sales",
  },
  [STEP_KEYS.CHOOSE_YOUR_PATH]: {
    title: "Choose Your Path",
    subtitle: "Try with existing or create new content",
  },
  [STEP_KEYS.PLAN_YOUR_CONTENT]: {
    title: "Plan Your Content",
    subtitle: "Choose a product from brands you like",
  },
  [STEP_KEYS.CREATE_YOUR_WISHLINK]: {
    title: "Create a Trial Wishlink",
    subtitle: "Try linking a product to your content",
  },
  [STEP_KEYS.SHARE_YOUR_WISHLINK]: {
    title: "Share Your Wishlink",
    subtitle: "Share on Instagram with your audience",
  },
  [STEP_KEYS.TEST_ENGAGE]: {
    title: "Test Engage",
    subtitle: "Check if Auto-DMs reach your followers",
  },
  [STEP_KEYS.YOURE_ALL_SET]: {
    title: "You're All Set",
    subtitle: "Your first earning journey begins",
  },
};

/**
 * The rows the stepper shows, and the rows the "x / y" counter counts.
 *
 * CAT-511: `choose_your_path` is a fork, not a checklist entry, so it is never
 * shown or counted — a brand_deals journey is five rows in the database but
 * reads as 4 steps, matching the design. `youre_all_set` is always present as
 * the trailing locked row ("Get noticed for Brand Collabs / Unlocks when you
 * finish setup"), not appended once the rest are done.
 *
 * While the path is still undecided the later rows do not exist yet, so they
 * are synthesised as LOCKED previews. `engage_setup` is previewed only when the
 * backend actually created it — it drops that step for the brand_deals intent,
 * and synthesising it would put a step back that the creator will never see.
 */
export const buildStepperSteps = (allSteps = [], path) => {
  if (path !== JOURNEY_PATHS.UNDECIDED) {
    return allSteps.filter((s) => s.key !== STEP_KEYS.CHOOSE_YOUR_PATH);
  }

  const hasEngageSetup = allSteps.some((s) => s.key === STEP_KEYS.ENGAGE_SETUP);

  return [...DEFAULT_STEP_KEYS, STEP_KEYS.YOURE_ALL_SET]
    .filter((key) => key !== STEP_KEYS.ENGAGE_SETUP || hasEngageSetup)
    .map(
      (key) =>
        allSteps.find((s) => s.key === key) ?? {
          key,
          status: STEP_STATUS.LOCKED,
        }
    );
};

// ---------------------------------------------------------------------------
// CAT-511 — PTFW V3 per-intent recopy.
//
// Everything below this line is TEST-bucket only. `getState` returns
// `ptfw_v3_variant` ("TEST" | "CONTROL" | null) alongside `is_eligible`, and
// `journey_state.creator_intent` ("brand_deals" | "earn_from_existing_posts" |
// "auto_dm_on_comments" | null). CONTROL, unbucketed, and legacy creators must
// keep seeing `STEP_META` / `STEP_ICONS` verbatim, so the resolvers below are
// the only place the two are allowed to diverge.
// ---------------------------------------------------------------------------

/** The AB key, shared by the intent gate, the entry point and the readout. */
export const PTFW_V3_FEATURE = "PTFWv3";

export const PTFW_V3_VARIANTS = {
  TEST: "TEST",
  CONTROL: "CONTROL",
};

// Figma 161:3213 — one how-you-earn frame per intent (148:19925
// earn_from_existing_posts, 148:19860 brand_deals, 148:20920
// auto_dm_on_comments). Titles and subtitles are transcribed verbatim.
export const HOW_YOU_EARN_META_BY_INTENT = {
  [INTENT_KEYS.BRAND_DEALS]: {
    title: "Brands pick creators who can sell",
    subtitle:
      "It starts with your first Wishlink. Learn how Wishlink helps you.",
  },
  [INTENT_KEYS.EARN_FROM_EXISTING_POSTS]: {
    title: "How you earn",
    subtitle: "Learn how Wishlink helps you earn",
  },
  [INTENT_KEYS.AUTO_DM_ON_COMMENTS]: {
    title: "Your Replies Can Earn Too",
    subtitle:
      "Create and share Wishlinks with Auto-DM. Every sale earns you commission.",
  },
};

// Recopied for every intent — the design shows the same pair on all three
// flows, so this is not keyed by intent.
export const ALL_SET_META_PTFW_V3 = {
  title: "Get noticed for Brand Collabs",
  subtitle: "Unlocks when you finish setup",
  // Figma 194:7131 — the row relabels itself once the journey completes.
  // `VerticalStepper` swaps to this the moment the step reads DONE.
  doneSubtitle: "You are now in the pool of creators brands pick from!",
};

// CAT-511 — the design drops the "Trial" framing the V2 experiment introduced.
// Same on all three intent flows, so it is not keyed by intent.
export const CREATE_WISHLINK_META_PTFW_V3 = {
  title: "Your First Wishlink",
  subtitle: "Link a product to your content",
};

// Figma shows a lightbulb on the active how-you-earn circle and a gift on the
// brand-collabs row, where today's map has a graduation cap and a trophy.
export const STEP_ICONS_PTFW_V3 = {
  ...STEP_ICONS,
  [STEP_KEYS.HOW_YOU_EARN]: "fa-solid fa-lightbulb",
  [STEP_KEYS.YOURE_ALL_SET]: "fa-solid fa-gift",
};

export const isPtfwV3Test = (ptfwV3Variant) =>
  ptfwV3Variant === PTFW_V3_VARIANTS.TEST;

/**
 * Step title/subtitle map for the current creator.
 *
 * Outside the TEST bucket this is `STEP_META` itself — same object identity,
 * so CONTROL cannot drift. Inside it, how-you-earn is keyed off the stored
 * intent and the all-set step is recopied. A TEST creator with no stored
 * intent (onboarded before CAT-510, so `creator_intent` is null) falls back to
 * today's how-you-earn pair rather than guessing an intent for them.
 *
 * @param {string|null|undefined} ptfwV3Variant - `data.ptfw_v3_variant`
 * @param {string|null|undefined} creatorIntent - `journey_state.creator_intent`
 */
export const resolveStepMeta = (ptfwV3Variant, creatorIntent) => {
  if (!isPtfwV3Test(ptfwV3Variant)) return STEP_META;

  const howYouEarn = HOW_YOU_EARN_META_BY_INTENT[creatorIntent];

  return {
    ...STEP_META,
    ...(howYouEarn ? { [STEP_KEYS.HOW_YOU_EARN]: howYouEarn } : {}),
    [STEP_KEYS.CREATE_YOUR_WISHLINK]: CREATE_WISHLINK_META_PTFW_V3,
    [STEP_KEYS.YOURE_ALL_SET]: ALL_SET_META_PTFW_V3,
  };
};

/**
 * Step circle glyphs for the current creator — `STEP_ICONS` outside the TEST
 * bucket, the Figma glyphs inside it.
 *
 * @param {string|null|undefined} ptfwV3Variant - `data.ptfw_v3_variant`
 */
export const resolveStepIcons = (ptfwV3Variant) =>
  isPtfwV3Test(ptfwV3Variant) ? STEP_ICONS_PTFW_V3 : STEP_ICONS;

// Steps shown before the creator has picked a path (CAT-509: the former
// SETUP_JOURNEY_V2 step list, now the only one).
export const DEFAULT_STEP_KEYS = [
  STEP_KEYS.ENGAGE_SETUP,
  STEP_KEYS.HOW_YOU_EARN,
  STEP_KEYS.CREATE_YOUR_WISHLINK,
  STEP_KEYS.TEST_ENGAGE,
];

export const BRAND_CAROUSEL = [
  { name: "Myntra", logo: "setup_journey_brand_myntra.png", commission: "15%" },
  {
    name: "Meesho",
    logo: "setup_journey_brand_meesho.png",
    commission: "15%",
  },
  { name: "Nykaa", logo: "setup_journey_brand_nykaa.png", commission: "15%" },
  {
    name: "Flipkart",
    logo: "setup_journey_brand_flipkart.png",
    commission: "15%",
  },
  {
    name: "Savana",
    logo: "setup_journey_brand_savana.png",
    commission: "15%",
  },
];

// Action types to hide from SuggestedActions for Setup Journey cohort
export const SETUP_JOURNEY_HIDDEN_ACTIONS = [
  "CREATE_WISHLINK",
  "SHARE_WISHLINK",
];
